/**
 * Allocation History Modal Component
 * Displays allocation history for selected transactions
 */

import { useMemo, useState, useEffect } from "react";
import { Settings, FileSpreadsheet, Search, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatDateDDMMYYYY, formatCurrency } from "@/lib/utils";
import { useCompany } from "@/contexts/CompanyContext";
import { type AllocationHistoryEntry } from "@/types/transaction";
import { TransactionTypeBadge } from "./transaction-type-badge";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { exportAllocationHistoryToExcel } from "@/lib/export-utils";
import { useAllocationHistoryGraphQL } from "@/hooks/useAllocationHistoryGraphQL";

interface AllocationHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactionId: number | null;
}

// Default column configuration
const DEFAULT_COLUMNS: ColumnDefinition[] = [
  { id: "accountNumber", label: "Account #", visible: true, order: 0 },
  { id: "customerName", label: "Customer Name", visible: true, order: 1 },
  { id: "transactionDate", label: "Transaction Date", visible: true, order: 2 },
  { id: "type", label: "Type", visible: true, order: 3 },
  { id: "referenceNumber", label: "Reference Number", visible: true, order: 4 },
  { id: "gross", label: "Gross", visible: true, order: 5 },
  { id: "allocatedValue", label: "Allocated Value", visible: true, order: 6 },
  { id: "allocatedDate", label: "Allocated Date", visible: true, order: 7 },
  { id: "currency", label: "Currency", visible: true, order: 8 },
  { id: "currencyGross", label: "Currency Gross", visible: true, order: 9 },
  { id: "currencyAllocValue", label: "Currency Alloc. Value", visible: true, order: 10 },
  { id: "allocationRef", label: "Allocation Ref", visible: true, order: 11 },
  { id: "userName", label: "User Name", visible: true, order: 12 },
];

export function AllocationHistoryModal({
  open,
  onOpenChange,
  transactionId,
}: Readonly<AllocationHistoryModalProps>) {
  const { selectedCompany } = useCompany();
  const [columns, setColumns] = useState<ColumnDefinition[]>(DEFAULT_COLUMNS);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Reset columns and search when modal opens
  useEffect(() => {
    if (open) {
      setColumns(DEFAULT_COLUMNS);
      setSearchQuery("");
    }
  }, [open]);

  // Fetch allocation history entries from GraphQL API using MainTransactionId filter
  const { data: fetchedEntries = [], isLoading } = useAllocationHistoryGraphQL(open ? transactionId : null);

  // Sort by allocated date (most recent first)
  const allocationHistoryEntries = useMemo(() => {
    return [...fetchedEntries].sort((a, b) => {
      const dateA = new Date(a.allocatedDate);
      const dateB = new Date(b.allocatedDate);
      return dateB.getTime() - dateA.getTime();
    });
  }, [fetchedEntries]);

  // Filter entries based on search query
  const filteredEntries = useMemo(() => {
    if (!searchQuery.trim()) {
      return allocationHistoryEntries;
    }

    const query = searchQuery.toLowerCase();
    return allocationHistoryEntries.filter((entry) => {
      return (
        entry.accountNumber.toLowerCase().includes(query) ||
        entry.customerName.toLowerCase().includes(query) ||
        entry.referenceNumber.toLowerCase().includes(query) ||
        entry.allocationRef.toLowerCase().includes(query) ||
        entry.userName.toLowerCase().includes(query) ||
        entry.type.toLowerCase().includes(query) ||
        formatCurrency(entry.gross, entry.currency).toLowerCase().includes(query) ||
        formatCurrency(entry.allocatedValue, entry.currency).toLowerCase().includes(query)
      );
    });
  }, [allocationHistoryEntries, searchQuery]);

  const totalAllocatedValue = useMemo(() => {
    return filteredEntries.reduce((sum, entry) => sum + entry.allocatedValue, 0);
  }, [filteredEntries]);

  // Get visible columns in order
  const visibleColumns = useMemo(() => {
    return columns
      .filter((col) => col.visible)
      .sort((a, b) => a.order - b.order);
  }, [columns]);

  const handleColumnConfigApply = (newColumns: ColumnDefinition[]) => {
    setColumns(newColumns);
    toast.success("Column configuration updated");
  };

  const handleColumnConfigReset = () => {
    setColumns(DEFAULT_COLUMNS);
    toast.success("Column configuration reset to default");
  };

  const handleExport = () => {
    try {
      const visibleColumnIds = visibleColumns.map((col) => col.id);
      exportAllocationHistoryToExcel(filteredEntries, undefined, visibleColumnIds);
      toast.success("Allocation history exported successfully");
    } catch (error) {
      toast.error("Failed to export allocation history");
      console.error("Export error:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <DialogTitle>Allocation History</DialogTitle>
              <DialogDescription>
                Viewing allocation history for selected transaction
                {allocationHistoryEntries.length > 0 && (
                  <span className="ml-2">
                    • {filteredEntries.length} of {allocationHistoryEntries.length} allocation{allocationHistoryEntries.length !== 1 ? 's' : ''} {searchQuery ? 'matching' : 'found'}
                  </span>
                )}
              </DialogDescription>
            </div>
            {allocationHistoryEntries.length > 0 && (
              <div className="flex items-center gap-2 mr-8">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleExport}
                  title="Export to Excel"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setColumnConfigOpen(true)}
                  title="Configure Columns"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        {allocationHistoryEntries.length > 0 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search allocation history..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        )}

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground">Loading allocation history...</p>
          </div>
        ) : allocationHistoryEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-full bg-muted p-3 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-muted-foreground"
              >
                <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
                <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
                <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">No Allocation History</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              The selected transaction does not have any allocation history records.
            </p>
          </div>
        ) : (
          <>
            <ScrollArea className="h-[calc(90vh-200px)]">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {visibleColumns.map((column) => (
                        <TableHead
                          key={column.id}
                          className={
                            column.id === "gross" ||
                            column.id === "allocatedValue" ||
                            column.id === "currencyGross" ||
                            column.id === "currencyAllocValue"
                              ? "text-right"
                              : ""
                          }
                        >
                          {column.label}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEntries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={visibleColumns.length} className="h-32 text-center">
                          <div className="flex flex-col items-center justify-center text-muted-foreground">
                            <Search className="h-8 w-8 mb-2 opacity-50" />
                            <p className="text-sm">No results found for "{searchQuery}"</p>
                            <p className="text-xs mt-1">Try adjusting your search terms</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredEntries.map((entry) => (
                        <TableRow key={entry.id}>
                          {visibleColumns.map((column) => {
                          const columnId = column.id;
                          
                          // Render cell based on column type
                          if (columnId === "accountNumber") {
                            return (
                              <TableCell key={columnId} className="font-medium">
                                {entry.accountNumber}
                              </TableCell>
                            );
                          } else if (columnId === "customerName") {
                            return <TableCell key={columnId}>{entry.customerName}</TableCell>;
                          } else if (columnId === "transactionDate") {
                            return (
                              <TableCell key={columnId}>
                                {formatDateDDMMYYYY(entry.transactionDate)}
                              </TableCell>
                            );
                          } else if (columnId === "type") {
                            return (
                              <TableCell key={columnId}>
                                <TransactionTypeBadge type={entry.type} />
                              </TableCell>
                            );
                          } else if (columnId === "referenceNumber") {
                            return (
                              <TableCell key={columnId} className="font-mono text-sm">
                                {entry.referenceNumber}
                              </TableCell>
                            );
                          } else if (columnId === "gross") {
                            return (
                              <TableCell key={columnId} className="text-right font-medium">
                                {formatCurrency(entry.gross, entry.currency)}
                              </TableCell>
                            );
                          } else if (columnId === "allocatedValue") {
                            return (
                              <TableCell key={columnId} className="text-right font-semibold text-green-600">
                                {formatCurrency(entry.allocatedValue, entry.currency)}
                              </TableCell>
                            );
                          } else if (columnId === "allocatedDate") {
                            return (
                              <TableCell key={columnId}>
                                {formatDateDDMMYYYY(entry.allocatedDate)}
                              </TableCell>
                            );
                          } else if (columnId === "currency") {
                            return (
                              <TableCell key={columnId}>
                                <Badge variant="outline">{entry.currency}</Badge>
                              </TableCell>
                            );
                          } else if (columnId === "currencyGross") {
                            return (
                              <TableCell key={columnId} className="text-right">
                                {formatCurrency(entry.currencyGross, entry.currencyCurrency)}
                              </TableCell>
                            );
                          } else if (columnId === "currencyAllocValue") {
                            return (
                              <TableCell key={columnId} className="text-right text-green-600">
                                {formatCurrency(entry.currencyAllocValue, entry.currencyCurrency)}
                              </TableCell>
                            );
                          } else if (columnId === "allocationRef") {
                            return (
                              <TableCell key={columnId} className="font-mono text-sm">
                                {entry.allocationRef}
                              </TableCell>
                            );
                          } else if (columnId === "userName") {
                            return <TableCell key={columnId}>{entry.userName}</TableCell>;
                          }
                          
                          return <TableCell key={columnId}>-</TableCell>;
                          })}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </ScrollArea>

            {/* Summary Footer */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                {searchQuery ? (
                  <>
                    Showing: <span className="font-semibold text-foreground">{filteredEntries.length}</span> of {allocationHistoryEntries.length} entries
                  </>
                ) : (
                  <>
                    Total Entries: <span className="font-semibold text-foreground">{allocationHistoryEntries.length}</span>
                  </>
                )}
              </div>
              <div className="text-sm">
                Total Allocated Value: <span className="font-semibold text-green-600">{formatCurrency(totalAllocatedValue, filteredEntries[0]?.currency || allocationHistoryEntries[0]?.currency || selectedCompany?.currency || 'GBP')}</span>
              </div>
            </div>
          </>
        )}

        {/* Column Configuration Dialog */}
        <ColumnConfigDialog
          open={columnConfigOpen}
          onOpenChange={setColumnConfigOpen}
          columns={columns}
          onApply={handleColumnConfigApply}
          onReset={handleColumnConfigReset}
        />
      </DialogContent>
    </Dialog>
  );
}
